import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import type { GroupTagUpdateItem } from '../components/GroupTagSelect';
import { GroupTagSelect } from '../components/GroupTagSelect';
import { Body } from '../components/Modal/Body';
import { ButtonSet } from '../components/Modal/ButtonSet';
import { Header } from '../components/Modal/Header';
import { Modal } from '../components/Modal/Modal';
import { Button } from '../components/presentation/Button';
import { Textarea } from '../components/presentation/Textarea';

type QuantAnalysisItem = {
  id: number;
  name: string;
  description?: string;
};

type EditQuantAnalysisItemData = {
  name: string;
  description: string;
  tags: GroupTagUpdateItem[];
};

type UseEditQuantAnalysisItemModalProps = {
  analysisItem: QuantAnalysisItem;
  projectId: number | undefined;
  quantAnalysisId: number;
  isOpen: boolean;
  onClose: () => void;
  onFetchTags: () => Promise<GroupTagUpdateItem[]>;
  onSearchTags: (query: string) => Promise<GroupTagUpdateItem[]>;
  onSubmit: (data: EditQuantAnalysisItemData) => Promise<void>;
  onSuccess?: () => void;
};

export const useEditQuantAnalysisItemModal = ({
  analysisItem,
  projectId,
  quantAnalysisId,
  isOpen,
  onClose,
  onFetchTags,
  onSearchTags,
  onSubmit,
  onSuccess,
}: UseEditQuantAnalysisItemModalProps) => {
  const [name, setName] = useState(analysisItem.name);
  const [description, setDescription] = useState(analysisItem.description || '');
  const [tags, setTags] = useState<GroupTagUpdateItem[]>([]);
  const [originalTags, setOriginalTags] = useState<GroupTagUpdateItem[]>([]);

  useEffect(() => {
    if (isOpen) {
      onFetchTags().then(tags => {
        setTags(tags);
        setOriginalTags(tags);
      }).catch((error) => {
        console.error('Failed to fetch tags:', error);
        setTags([]);
        setOriginalTags([]);
      });
    }
  }, [isOpen, onFetchTags]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      return onSubmit({
        name,
        description,
        tags,
      });
    },
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setName(event.target.value);
  }, []);

  const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  }, []);

  const handleTagsChange = useCallback((newTags: GroupTagUpdateItem[]) => {
    setTags(newTags);
  }, []);

  const tagsHaveChanged = useCallback(() => {
    // Check if any tags were added or removed (comparing by id)
    const tagIds = new Set(tags.map(t => t.id));
    const originalTagIds = new Set(originalTags.map(t => t.id));
    
    // Check if sets have different sizes or different content
    if (tagIds.size !== originalTagIds.size) {
      return true;
    }
    
    // Since sizes are equal, check if all tags in current set exist in original set
    return [...tagIds].some(id => !originalTagIds.has(id));
  }, [tags, originalTags]);

  const hasChanges =
    name !== analysisItem.name ||
    description !== (analysisItem.description || '') ||
    tagsHaveChanged();

  const canSubmit = name.trim() !== '' && hasChanges && !mutation.isPending;

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      mutation.mutate();
    }
  }, [mutation, canSubmit]);

  const EditModalComponent = (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Body>
        <Header text="Edit Analysis Tab" />

        <Container>
          <Field>
            <Label>
              Analysis Tab Name
            </Label>
            <Textarea
              value={name}
              onChange={handleNameChange}
              placeholder="Enter analysis tab name"
              disabled={mutation.isPending}
              minRows={1}
            />
          </Field>

          <Field>
            <Label>
              Analysis Description
            </Label>
            <Textarea
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter a description of what is contained in this analysis tab"
              disabled={mutation.isPending}
              minRows={1}
            />
          </Field>

          <div>
            <Label>
              Tags
            </Label>
            <GroupTagSelect
              onSearch={onSearchTags}
              onChange={handleTagsChange}
              value={tags}
            />
          </div>
        </Container>
      </Body>

      <ButtonSet>
        <Button.Destructive
          onClick={onClose}
          disabled={mutation.isPending}
          variant="brick"
        >
          Cancel
        </Button.Destructive>

        <Button.Primary
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="brick"
        >
          {mutation.isPending ? 'Saving...' : 'Submit'}
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );

  return EditModalComponent;
};

const Container = styled.div({
  marginTop: 20,
  textAlign: 'left',
});

const Field = styled.div({
  marginBottom: 16,
});

const Label = styled.label(({ theme }) => ({
  display: 'block',
  marginBottom: 8,
  fontSize: 16,
  fontFamily: theme.fonts.semiBold,
}));
